import { BookOpen, Download, Eye, Pencil, Share2, Trash2, type LucideIcon } from 'lucide-react';
import type * as React from 'react';

import { cn } from '../lib/utils';

/** Discriminating literal union for the event-row action glyph. */
export type EventIconName = 'view' | 'read' | 'write' | 'download' | 'share' | 'delete';

export interface EventIconProps {
  name: EventIconName;
  className?: string;
  /** Optional aria-label. When omitted, the glyph is decorative (`aria-hidden`). */
  'aria-label'?: string;
}

const NAME_TO_GLYPH = {
  view: Eye,
  read: BookOpen,
  write: Pencil,
  download: Download,
  share: Share2,
  delete: Trash2,
} as const satisfies Readonly<Record<EventIconName, LucideIcon>>;

/**
 * EventIcon — view / download / share / delete fixed at 14px per Figma registry
 * (design.md § 3.2). Used inside event-action rows in tables and popovers.
 */
export const EventIcon: React.FC<EventIconProps> = ({
  name,
  className,
  'aria-label': ariaLabel,
}) => {
  const Glyph = NAME_TO_GLYPH[name];
  return (
    <Glyph
      width={14}
      height={14}
      className={cn(className)}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    />
  );
};

EventIcon.displayName = 'EventIcon';
